import { isArray, isEmpty, isObject, uniq } from "lodash";
import { createSelector } from "reselect";

import {
  getFieldsWithAlternativeValues,
  getNamespacedCompareFieldNames,
} from "components/Form/FormikControls/lib";

import {
  AlternativeComparableKeys,
  AlternativeType,
} from "constants/businesses";
import { IntegrationTypes } from "constants/integrations";

import { EMPTY_OBJECT } from "lib";

import { getDateFromISO8601DateString } from "lib/timeFormats";

import { getIsSaleyardAdmin } from "../auth";
import { getActiveBusinesses } from "../businesses";
import { getActiveLivestockAgentDeployment } from "../deploymentActiveAgent";
import {
  selectBuyerIdBySaleLotIdLookup,
  selectorInvoiceToBusinessIdBySaleLotIdLookup,
  selectorThirdPartyBusinessIdBySaleLotIdLookup,
  selectVendorIdBySaleLotIdLookup,
} from "../indexes";
import { selectIntegrationBusinessesByBusinessIdByTypeLookup } from "../integrationBusinesses";
import { selectResolvedInterestSettingsByBusinessIdLookup } from "../interest";
import { selectorCategorisedBusinessIdsFromCurrentSaleManualAdjustments } from "../manualAdjustments";
import {
  getBusinesses,
  getBusinessRelations,
  getDeployments,
  getProperties,
  getSaleyards,
} from "../root";
import { selectVendorCommissionBandsByDeploymentBusinessIdLookup } from "../vendorCommissionBands";

export const BusinessTypes = {
  BUYER: "Buyer",
  INVOICEE: "Invoicee",
  VENDOR: "Vendor",
  TRANSPORTER: "Transporter",
  THIRD_PARTY: "Third Party",
  SUNDRY_TO: "Sundry To",
  SUNDRY_FROM: "Sundry From",
};

const selectBusinessesIdsInSaleByType = createSelector(
  [
    selectVendorIdBySaleLotIdLookup,
    selectBuyerIdBySaleLotIdLookup,
    selectorInvoiceToBusinessIdBySaleLotIdLookup,
    selectorThirdPartyBusinessIdBySaleLotIdLookup,
    selectorCategorisedBusinessIdsFromCurrentSaleManualAdjustments,
  ],
  (
    vendorIdBySaleLotIdLookup,
    buyerIdBySaleLotIdLookup,
    invoiceToBusinessIdBySaleLotIdLookup,
    thirdPartyBusinessIdBySaleLotIdLookup,
    categorisedBusinessIdsFromCurrentSaleManualAdjustments,
  ) => {
    return {
      [BusinessTypes.BUYER]: uniq(Object.values(buyerIdBySaleLotIdLookup)),
      [BusinessTypes.INVOICEE]: uniq(
        Object.values(invoiceToBusinessIdBySaleLotIdLookup),
      ),
      [BusinessTypes.THIRD_PARTY]: uniq(
        Object.values(thirdPartyBusinessIdBySaleLotIdLookup),
      ),
      [BusinessTypes.VENDOR]: uniq(Object.values(vendorIdBySaleLotIdLookup)),
      [BusinessTypes.SUNDRY_FROM]: uniq(
        categorisedBusinessIdsFromCurrentSaleManualAdjustments.from.filter(
          Boolean,
        ),
      ),
      [BusinessTypes.SUNDRY_TO]: uniq(
        categorisedBusinessIdsFromCurrentSaleManualAdjustments.to.filter(
          Boolean,
        ),
      ),
    };
  },
);

export const expandAlternatives = (obj, deploymentsLookup, saleyardsLookup) => {
  if (obj.alternatives) {
    obj.alternatives = obj.alternatives.map(alternative => {
      if (alternative.source.type === AlternativeType.Deployment) {
        return Object.assign({}, alternative, {
          lastModified: alternative.lastModified
            ? getDateFromISO8601DateString(alternative.lastModified)
            : null,
          sourceName: deploymentsLookup[alternative.source.id]?.name,
        });
      } else {
        return Object.assign({}, alternative, {
          lastModified: alternative.lastModified
            ? getDateFromISO8601DateString(alternative.lastModified)
            : null,
          sourceName: saleyardsLookup[alternative.source.id]?.name,
        });
      }
    });
  }
  Object.keys(obj).forEach(key => {
    if (isArray(obj[key])) {
      obj[key] = obj[key].map(k =>
        isObject(k)
          ? expandAlternatives(k, deploymentsLookup, saleyardsLookup)
          : k,
      );
    }
  });

  return obj;
};

export const selectBusinessesInSaleByIdLookup = createSelector(
  [
    getBusinesses,
    getProperties,
    selectBusinessesIdsInSaleByType,
    getDeployments,
    getSaleyards,
    selectResolvedInterestSettingsByBusinessIdLookup,
    selectVendorCommissionBandsByDeploymentBusinessIdLookup,
    getActiveLivestockAgentDeployment,
    selectIntegrationBusinessesByBusinessIdByTypeLookup,
  ],
  (
    businesses,
    properties,
    businessIdsByType,
    deploymentsLookup,
    saleyardsLookup,
    resolvedInterestSettingsByBusinessId,
    vendorCommissionsByDeploymentBusinessId,
    activeLivestockAgentDeployment,
    integrationBusinessesByTypeByBusinessIdLookup,
  ) => {
    const result = {};

    Object.entries(businessIdsByType).forEach(([businessType, businessIds]) => {
      businessIds.forEach(businessId => {
        const business = expandAlternatives(
          { ...(businesses[businessId] || {}) },
          deploymentsLookup,
          saleyardsLookup,
        );

        if (isEmpty(business)) {
          return;
        }

        const xeroIntegrationBusiness =
          integrationBusinessesByTypeByBusinessIdLookup[
            IntegrationTypes.Xero
          ]?.[business.id]?.[0] || null;

        const lastModified = business.lastModified
          ? getDateFromISO8601DateString(business.lastModified)
          : null;
        const lastReviewed = business.lastReviewed
          ? getDateFromISO8601DateString(business.lastReviewed)
          : null;

        const hasAlternativeNewerThanReviewed = business.alternatives.some(
          altBusiness => altBusiness.lastModified > lastReviewed,
        );
        const hasAlternativeNewerThanModified = business.alternatives.some(
          altBusiness => altBusiness.lastModified > lastModified,
        );

        const outOfSyncFields = [].concat(
          getFieldsWithAlternativeValues(AlternativeComparableKeys, business),
          ...business.properties.map((property, idx) =>
            getFieldsWithAlternativeValues(
              getNamespacedCompareFieldNames(
                "properties",
                AlternativeComparableKeys,
                idx,
              ),
              property,
            ),
          ),
          ...business.emailRecipients.map((emailRecipient, idx) =>
            getFieldsWithAlternativeValues(
              getNamespacedCompareFieldNames(
                "emailRecipients",
                AlternativeComparableKeys,
                idx,
              ),
              emailRecipient,
            ),
          ),
        );

        if (businessId in result) {
          result[businessId].roleInSale.push(businessType);
        } else {
          result[businessId] = {
            business,
            roleInSale: [businessType],
            outOfSyncFields,
            lastModified,
            lastReviewed,
            hasAlternativeNewerThanReviewed,
            hasAlternativeNewerThanModified,
            xeroIntegrationBusiness,
            deployment: activeLivestockAgentDeployment,
            interestSettings: resolvedInterestSettingsByBusinessId[businessId],
            vendorCommissionBands:
              vendorCommissionsByDeploymentBusinessId[
                business?.deploymentBusinessId
              ] || [],
          };
        }
      });
    });
    return result;
  },
);

export const getBusinessesInSaleById = businessId => state =>
  selectBusinessesInSaleByIdLookup(state)[businessId];

export const selectBusinessesInSaleAggridData = createSelector(
  [selectBusinessesInSaleByIdLookup],
  businessesInSaleLookup => Object.values(businessesInSaleLookup),
);

export const selectOptionsForBusinessField = createSelector(
  [
    getBusinesses,
    selectVendorIdBySaleLotIdLookup,
    selectBuyerIdBySaleLotIdLookup,
    selectorInvoiceToBusinessIdBySaleLotIdLookup,
    selectorThirdPartyBusinessIdBySaleLotIdLookup,
    selectorCategorisedBusinessIdsFromCurrentSaleManualAdjustments,
    getActiveBusinesses,
    getIsSaleyardAdmin,
    getProperties,
    getBusinessRelations,
  ],
  (
    businessByIdLookup,
    vendorIdBySaleLotIdLookup,
    buyerIdBySaleLotIdLookup,
    invoiceToBusinessIdBySaleLotIdLookup,
    thirdPartyBusinessIdBySaleLotIdLookup,
    categorisedBusinessIdsFromCurrentSaleManualAdjustments,
    activeBusinessByIdLookup,
    isSaleyardAdmin,
    getPropertyLookup,
    businessRelationsLookup,
  ) => {
    const businesses = Object.assign({}, businessByIdLookup) || EMPTY_OBJECT;

    for (const vendorId of Object.values(vendorIdBySaleLotIdLookup)) {
      if (businesses[vendorId]) {
        businesses[vendorId].isVendor = true;
        businesses[vendorId].inSale = true;
      }
    }

    for (const buyerId of Object.values(buyerIdBySaleLotIdLookup)) {
      if (businesses[buyerId]) {
        businesses[buyerId].isBuyer = true;
        businesses[buyerId].inSale = true;
      }
    }

    for (const invoiceToId of Object.values(
      invoiceToBusinessIdBySaleLotIdLookup,
    )) {
      if (businesses[invoiceToId]) {
        businesses[invoiceToId].invoiceTo = true;
        businesses[invoiceToId].isBuyer = true;
        businesses[invoiceToId].inSale = true;
      }
    }

    for (const thirdPartyId of Object.values(
      thirdPartyBusinessIdBySaleLotIdLookup,
    )) {
      if (businesses[thirdPartyId]) {
        businesses[thirdPartyId].thirdParty = true;
        businesses[thirdPartyId].isBuyer = true;
        businesses[thirdPartyId].inSale = true;
      }
    }

    for (const activeBusinessId of Object.values(activeBusinessByIdLookup)) {
      if (businesses[activeBusinessId]) {
        businesses[activeBusinessId].isActive = true;
      }
    }

    for (const businessRelation of Object.values(businessRelationsLookup)) {
      if (businesses[businessRelation.relatedFromId]) {
        businesses[businessRelation.relatedFromId].isRelatedFrom = true;
      }
      if (businesses[businessRelation.relatedToId]) {
        businesses[businessRelation.relatedToId].isRelatedTo = true;
      }
    }

    for (const manualAdjustmentBusinessIds of Object.values(
      categorisedBusinessIdsFromCurrentSaleManualAdjustments,
    )) {
      // Broken up into from/to, but we will just classify as being involved in sundry.
      for (const manualAdjustmentBusinessId of manualAdjustmentBusinessIds) {
        if (businesses[manualAdjustmentBusinessId]) {
          businesses[manualAdjustmentBusinessId].isSundry = true;
        }
      }
    }

    for (const business of Object.values(businesses)) {
      const inActive = business.isActive ? "" : " (INACTIVE)";
      const code = isSaleyardAdmin
        ? business.shortCodeSaleyard
        : business.shortCode;

      business.properties =
        business?.properties?.map(p => ({
          PIC: getPropertyLookup[p.id]?.PIC || "",
          ...p,
        })) || [];
      business.label = `${code ? `(${code})` : ""} ${business.name}${inActive}`;
    }

    return businesses;
  },
);
